// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { CurrencyCode } from "@outschool/gql-backend-generated";
import {
  ActivityEnrollQueryQuery,
  CommonEnrollmentFormActivityFragmentFragment,
  CommonEnrollmentFormSectionFragmentFragment,
  CreateEnrollmentsMutation,
  CreateEnrollmentsMutationVariables,
  GetLearnerItems_EnrollmentFragment,
  ParentChildrenQuery
} from "@outschool/gql-frontend-generated";
import { gql, useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { useBooleanState } from "@outschool/ui-utils";
import * as React from "react";
import { useEffect, useState } from "react";

import * as Enrollment from "../../../shared/Enrollment";
import * as Section from "../../../shared/Section";
import { createEnrollmentsMutation } from "../../queries/EnrollmentMutations";
import {
  useAcceptPaymentWithPurchaseOrder,
  useSettleOrder
} from "../../queries/PaymentMutations";
import EnrollmentForm from "./EnrollmentForm";
import { getLearnerItems } from "./enrollmentForm/learnerItems/getLearnerItems";
import { OrderProvider, useOrderContext } from "./OrderContext";

type EnrollmentType = NonNullable<
  ParentChildrenQuery["parentByEmail"]
>["enrollments"][number];

type ActivityEnrollWithCharterPurchaseOrderProps = {
  activity: CommonEnrollmentFormActivityFragmentFragment;
  enrollingParentEnrollments: GetLearnerItems_EnrollmentFragment[];
  enrollmentsInOtherSections: GetLearnerItems_EnrollmentFragment[];
  parentByEmail?: ParentChildrenQuery["parentByEmail"];
  charterParentInput: {
    name: string;
    email: string;
  };
  disabled: boolean;
  onClose: () => void;
  onEnrollmentInProgressChange: (b: boolean) => void;
  charterSchool: { uid: string };
  purchaseOrderId: string;
  intercomUrl: string;
  onPaymentComplete: (sectionUid: string) => void;
  section?: CommonEnrollmentFormSectionFragmentFragment | null;
  selfPacedStartTime?: Date | null;
  setPurchaseOrderCoverage?: React.Dispatch<React.SetStateAction<Date | null>>;
  purchaseOrderCoverage?: Date | null;
};

function ActivityEnrollWithCharterPurchaseOrder({
  activity,
  section,
  parentByEmail,
  charterParentInput,
  disabled,
  onClose,
  onEnrollmentInProgressChange,
  charterSchool,
  purchaseOrderId,
  intercomUrl,
  onPaymentComplete,
  enrollingParentEnrollments,
  enrollmentsInOtherSections,
  purchaseOrderCoverage,
  selfPacedStartTime,
  setPurchaseOrderCoverage
}: ActivityEnrollWithCharterPurchaseOrderProps) {
  const acceptPaymentWithPurchaseOrder = useAcceptPaymentWithPurchaseOrder();
  const settleOrder = useSettleOrder();
  const { pricingBreakdown, pricingBreakdownError, ongoingStartTime } =
    useOrderContext();

  const [isPaying, setIsPaying] = React.useState(false);
  const [
    enrollmentInProgress,
    setEnrollmentInProgress,
    clearEnrollmentInProgress
  ] = useBooleanState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    onEnrollmentInProgressChange(enrollmentInProgress);
  }, [enrollmentInProgress, onEnrollmentInProgressChange]);
  const [createEnrollments] = useMutation<
    CreateEnrollmentsMutation,
    CreateEnrollmentsMutationVariables
  >(createEnrollmentsMutation);

  const handleEnrollClick = async (
    parentByEmail: $TSFixMe,
    charterParentInput: $TSFixMe,
    enrollmentInputs: $TSFixMe
  ) => {
    if (!pricingBreakdown?.totalCents) {
      return;
    }
    setEnrollmentInProgress();
    const parentInput = {
      name: (parentByEmail && parentByEmail.name) || charterParentInput.name, // let manual input override when name is null
      email: (parentByEmail || charterParentInput).email
    };
    try {
      const { data } = await createEnrollments({
        variables: {
          orderInput: {
            activityUid: activity.uid,
            sectionUid: section?.uid,
            enrollmentInputs,
            organizationUid: charterSchool?.uid,
            charterParentInput: parentInput,
            ongoingStartTime,
            purchaseOrderCoverage,
            currency_code: CurrencyCode.Usd,
            exchange_rate: 1,
            selfPacedStartTime
          },
          isPrepay: true
        }
      });
      const orderSlugId = data?.createEnrollments.orderSlugId!;
      clearEnrollmentInProgress();
      setIsPaying(true);
      await acceptPaymentWithPurchaseOrder(
        orderSlugId,
        pricingBreakdown.totalCents,
        charterSchool?.uid,
        intercomUrl,
        purchaseOrderId
      );
      await settleOrder(orderSlugId, undefined, ongoingStartTime ?? undefined);
      const sectionUid =
        section?.uid ?? data?.createEnrollments.enrollments?.[0].section_uid;
      setIsPaying(false);
      onPaymentComplete(sectionUid!);
    } catch (error) {
      setError(error);
      clearEnrollmentInProgress();
    }
  };

  return (
    <EnrollmentForm
      activity={activity}
      lastSubmittedEnrollments={[]}
      section={section ?? null}
      leader={section?.leader ?? null}
      enrollingParent={
        parentByEmail as unknown as ActivityEnrollQueryQuery["currentUser"]
      }
      isUsingPurchaseOrder={true}
      enrollingParentEnrollments={enrollingParentEnrollments}
      enrollmentsInOtherSections={enrollmentsInOtherSections}
      onClose={onClose}
      error={error || pricingBreakdownError}
      disabled={disabled}
      requestAgeRangeException={
        () => {} /* Not supported for Charter schools */
      }
      enrollmentInProgress={isPaying || enrollmentInProgress}
      onEnrollClick={enrollmentInputs =>
        handleEnrollClick(parentByEmail, charterParentInput, enrollmentInputs)
      }
      setPurchaseOrderCoverage={setPurchaseOrderCoverage}
      disableOnSubmit={false}
      filledSpaceCount={0} // TODO: WTF
    />
  );
}

export default function ActivityEnrollWithCharterPurchaseOrderWrapper(
  props: Omit<
    ActivityEnrollWithCharterPurchaseOrderProps,
    "enrollingParentEnrollments" | "enrollmentsInOtherSections"
  >
) {
  const { currentUser } = useSession();
  const { parentByEmail, activity, section, purchaseOrderCoverage } = props;

  const enrollingParentEnrollments: NonNullable<EnrollmentType>[] =
    parentByEmail
      ? parentByEmail.enrollments.filter(
          (e: EnrollmentType | null): e is NonNullable<EnrollmentType> =>
            !!e && e.section_uid === section?.uid && Enrollment.isValid(e)
        )
      : [];

  // Used to warn parent that they have already enrolled
  // to this class but to another section
  const enrollmentsInOtherSections: NonNullable<EnrollmentType>[] = (
    parentByEmail?.enrollments ?? []
  ).filter(
    (e: EnrollmentType | null): e is NonNullable<EnrollmentType> =>
      !!e &&
      e.section?.activity_uid === activity.uid &&
      e.section_uid !== section?.uid &&
      Enrollment.isValid(e)
  );

  if (!currentUser) {
    return null;
  }

  return (
    <OrderProvider
      buyingUserUid={currentUser.uid}
      activity={activity}
      section={section}
      enrollingParent={
        parentByEmail as unknown as ActivityEnrollQueryQuery["currentUser"]
      }
      lastSubmittedEnrollments={[]}
      conflictingEnrollments={[]}
      enrollmentsInOtherSections={enrollmentsInOtherSections}
      enrollingParentEnrollments={enrollingParentEnrollments}
      isUsingPurchaseOrder={true}
      purchaseOrderCoverage={purchaseOrderCoverage}
      ignoreStrictAgeRange={!!section && Section.ignoresStrictAgeRange(section)}
      sectionPrepaidOngoingPeriodOptions={section?.prepaidOngoingPeriodOptions}
    >
      <ActivityEnrollWithCharterPurchaseOrder
        {...props}
        enrollingParentEnrollments={enrollingParentEnrollments}
        enrollmentsInOtherSections={enrollmentsInOtherSections}
      />
    </OrderProvider>
  );
}
ActivityEnrollWithCharterPurchaseOrderWrapper.fragments = {
  enrollment: gql`
    fragment ActivityEnrollWithCharterPurchaseOrder_enrollment on Enrollment {
      uid
      section_uid
      confirmed_at
      withdrawn_at
      section {
        uid
        activity_uid
      }
      isPrepay
      ...getLearnerItems_enrollment
    }
    ${getLearnerItems.fragments.enrollment}
  `
};
